import clsx from 'clsx';
import { useMemo } from 'react';
import { getSegmentsFromSteps } from './arc-drawing.utils';
import { CircularProgress } from './CircularProgress';
import { labels as labels_i18n } from './labels.i18n';
import styles from './ProgressIndicator.module.scss';
import {
  ProgressIndicatorLabels,
  ProgressProps,
} from './ProgressIndicator.types';

type ProgressIndicatorProps = {
  step: number;
  steps: ProgressProps[];
  labels?: ProgressIndicatorLabels;
  activeStepOnly?: boolean;
};

export const ProgressIndicator = ({
  step,
  steps,
  labels = labels_i18n.en,
  activeStepOnly,
}: ProgressIndicatorProps) => {
  const pastHiddenSteps = useMemo(
    () =>
      steps.reduce((acc, curr, i) => {
        if (i >= step) return acc;
        if (curr.hidden) return acc + 1;
        return acc;
      }, 0),
    [step, steps]
  );

  const _steps = useMemo(() => steps.filter((step) => !step.hidden), [steps]);

  const activeStep = steps.at(step - 1);

  const displayActiveStepNumber = step - pastHiddenSteps;

  const nextStep =
    steps.at(step)?.customNextStepIndicator ??
    steps.at(step)?.title ??
    labels.complete;

  const totalSteps = _steps.length;

  const progressSegments = getSegmentsFromSteps(steps);

  const displayText = activeStepOnly
    ? `${displayActiveStepNumber}`
    : `${displayActiveStepNumber} / ${totalSteps}`;

  return (
    <div className={styles.progress}>
      <div className={styles.indicator}>
        {progressSegments.map((segment) => (
          <CircularProgress
            key={`${segment.from}-${segment.to}`}
            {...segment}
          />
        ))}
        <div className={clsx(styles.step, styles.isAbsolute)}>
          {displayText}
        </div>
      </div>

      <div>
        <div className={styles.title}>{activeStep?.title}</div>
        <div className={styles.nextTitle}>
          {labels.next}: {nextStep}
        </div>
      </div>
    </div>
  );
};
